export interface MapLayerPopoverRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
}

export interface MapLayerPopoverViewport {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface MapLayerPopoverPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: 'above' | 'below';
}

/** A screen-space rectangle that a map-layer menu must not visually cover. */
export type MapLayerPopoverAvoidRect = MapLayerPopoverRect;

export const MAP_LAYER_POPOVER_GUTTER_PX = 12;
export const MAP_LAYER_POPOVER_OPTION_HEIGHT_PX = 60;
export const MAP_LAYER_POPOVER_MAX_WIDTH_PX = 420;
export const MAP_LAYER_POPOVER_MAX_HEIGHT_PX = 328;

/**
 * Places a portaled, fixed map-layer menu within the *visual* viewport. The
 * returned maxHeight is deliberately compact for the finite map-layer lists;
 * scroll becomes available only when physical viewport space requires it.
 */
export function computeMapLayerPopoverPosition({
  trigger,
  viewport,
  optionCount,
  optionHeight = MAP_LAYER_POPOVER_OPTION_HEIGHT_PX,
  gutter = MAP_LAYER_POPOVER_GUTTER_PX,
  avoidRects = [],
}: {
  trigger: MapLayerPopoverRect;
  viewport: MapLayerPopoverViewport;
  optionCount: number;
  optionHeight?: number;
  gutter?: number;
  avoidRects?: readonly MapLayerPopoverAvoidRect[];
}): MapLayerPopoverPosition {
  const menuContentHeight = Math.min(
    MAP_LAYER_POPOVER_MAX_HEIGHT_PX,
    Math.max(optionHeight + 8, optionCount * optionHeight + 8),
  );
  const viewportRight = viewport.left + viewport.width;
  const viewportBottom = viewport.top + viewport.height;
  const viewportMidX = viewport.left + viewport.width / 2;
  const viewportMidY = viewport.top + viewport.height / 2;
  let safeTop = viewport.top + gutter;
  let safeBottom = viewportBottom - gutter;
  let safeLeft = viewport.left + gutter;
  let safeRight = viewportRight - gutter;

  for (const rect of avoidRects) {
    const rectWidth = Math.max(0, rect.right - rect.left);
    const rectHeight = Math.max(0, rect.bottom - rect.top);
    const isFullWidthBand = rectWidth >= viewport.width * 0.6;
    const isRightDock = !isFullWidthBand && rect.left >= viewportMidX && rectHeight > rectWidth;

    if (isFullWidthBand && rect.top <= viewportMidY) {
      safeTop = Math.max(safeTop, rect.bottom + gutter);
    }
    if (isFullWidthBand && rect.bottom >= viewportMidY) {
      safeBottom = Math.min(safeBottom, rect.top - gutter);
    }
    if (isRightDock) {
      safeRight = Math.min(safeRight, rect.left - gutter);
    }
  }

  // A trigger may live inside an excluded bottom bar. Clamp the anchor to the
  // usable map rectangle before deciding whether the menu can open above/below.
  safeBottom = Math.max(safeTop, safeBottom);
  safeRight = Math.max(safeLeft, safeRight);
  const anchorTop = Math.min(Math.max(trigger.top, safeTop), safeBottom);
  const anchorBottom = Math.min(Math.max(trigger.bottom, safeTop), safeBottom);
  const availableBelow = Math.max(0, safeBottom - anchorBottom);
  const availableAbove = Math.max(0, anchorTop - safeTop);
  const placement: 'above' | 'below' = availableBelow >= menuContentHeight || availableBelow >= availableAbove
    ? 'below'
    : 'above';
  const availableHeight = placement === 'below' ? availableBelow : availableAbove;
  const maxHeight = Math.max(0, Math.min(menuContentHeight, availableHeight));
  const width = Math.min(
    Math.max(240, trigger.width),
    MAP_LAYER_POPOVER_MAX_WIDTH_PX,
    Math.max(0, safeRight - safeLeft),
  );
  const left = Math.min(
    Math.max(safeLeft, trigger.left),
    Math.max(safeLeft, safeRight - width),
  );
  const top = placement === 'below'
    ? Math.min(anchorBottom + 6, Math.max(safeTop, safeBottom - maxHeight))
    : Math.max(safeTop, anchorTop - maxHeight - 6);

  return { top, left, width, maxHeight, placement };
}
